import { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";

function StripeForm({ booking, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const [paying, setPaying] = useState(false);

    const pay = async () => {
        if (!stripe || !elements) return;
        setPaying(true);
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
            });
            if (error) {
                toast.error(error.message || t("book.paymentFailed"));
                setPaying(false);
                return;
            }
            if (paymentIntent && paymentIntent.status === "succeeded") {
                await api.post("/payments/stripe/finalize", {
                    booking_id: booking.id,
                    payment_intent_id: paymentIntent.id,
                });
                onSuccess();
                return;
            }
            toast.error(t("book.paymentFailed"));
            setPaying(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || t("book.paymentFailed"));
            setPaying(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="stripe-payment-form">
            <PaymentElement />
            <button
                onClick={pay}
                disabled={paying || !stripe}
                data-testid="stripe-pay-btn"
                className="w-full px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
                {paying ? <><Loader2 size={14} className="animate-spin" /> {t("book.paying")}</>
                    : t("book.payNow", formatMoney(booking.total_price))}
            </button>
            <p className="text-[11px] text-muted2 inline-flex items-center gap-1.5 justify-center w-full">
                <ShieldCheck size={12} className="text-forest" /> {t("book.securePayment")}
            </p>
        </div>
    );
}

export default function PaymentDialog({ open, booking, clientSecret, publishableKey, onClose, onSuccess }) {
    const { t } = useT();
    const stripePromise = useMemo(
        () => (publishableKey ? loadStripe(publishableKey) : null),
        [publishableKey]
    );

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">{t("book.cardEntry")}</DialogTitle>
                    <DialogDescription>{booking?.cabin_name}</DialogDescription>
                </DialogHeader>
                {clientSecret && stripePromise ? (
                    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
                        <StripeForm booking={booking} onSuccess={onSuccess} />
                    </Elements>
                ) : (
                    <div className="py-10 text-center text-muted2 text-sm inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 size={16} className="animate-spin" /> {t("book.paying")}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
